import { AssetConfigurationsService } from './../../asset-configurations.service';
import { Component, Input, ViewChild, OnInit, AfterViewInit } from '@angular/core';
import { FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { FileInfo, RemovingEventArgs, SelectedEventArgs, Uploader, UploaderModule } from '@syncfusion/ej2-angular-inputs';
import { ToastService } from 'src/app/base/services/toast.service';
import { MimeTypeHelper } from 'src/app/base/shared/mime-type.helper';
import { DropDownConstantsFactory } from 'src/app/base/shared/syncfusion/dropdown-constants-factory';
import { EnumTranslationService } from 'src/app/base/services/enum-translation.service';
import { AssetType } from 'src/app/base/models/models.swagger.generated';
import { createElement, isNullOrUndefined } from '@syncfusion/ej2-base';
import { DropDownListComponent, MultiSelectModule, DropDownListModule } from '@syncfusion/ej2-angular-dropdowns';
import { AssetUploadEditorFormType } from './asset-upload-editor-form-type';
import { SwitchComponent, SwitchModule } from '@syncfusion/ej2-angular-buttons';
import { DropDownItemTyped } from 'src/app/base/shared/drop-down-item.model';
import { TranslateModule } from '@ngx-translate/core';
import { YesNoGroupButtonComponent } from '../../../../base/shared/yes-no-group-button/yes-no-group-button.component';
import { AppErrorMessagesComponent } from '../../../../base/error-messages/app-error-messages.component';
import { CommonModule } from '@angular/common';

@Component({
    selector: 'app-asset-upload-editor',
    templateUrl: './asset-upload-editor.component.html',
    styleUrls: ['./asset-upload-editor.component.scss'],
    imports: [ReactiveFormsModule, CommonModule, UploaderModule, AppErrorMessagesComponent, MultiSelectModule, DropDownListModule, YesNoGroupButtonComponent, SwitchModule, TranslateModule]
})
export class AssetUploadEditorComponent implements OnInit, AfterViewInit {
  @Input()
  public assetUploadEditorForm: FormGroup<AssetUploadEditorFormType>;

  @Input()
  public isBrandLogoMode: boolean = false;

  @ViewChild('defaultupload')
  public defaultupload: Uploader;

  @ViewChild('typeDropdown')
  public typeDropdown: DropDownListComponent;

  @ViewChild("replaceCustomizedLogos")
  public replaceCustomizedLogos: SwitchComponent;

  public parentElement: HTMLElement;

  public allowedExtensions: string = '';
  public deniedExtensions: string = '';
  public languages: string[] = [];
  public supportedResolutions: number[] = [];

  public enumDropdownSettings = DropDownConstantsFactory.createNumericEnumDropdownSettings();

  public shopIds: string[] = [];

  public get isImageTypeSelected(): boolean {
    return this.assetUploadEditorForm?.controls?.type.value === AssetType.Image;
  }

  public get isLogoTypeSelected(): boolean {
    return this.assetUploadEditorForm?.controls?.type.value === AssetType.Logo;
  }

  public assetTypes: DropDownItemTyped<typeof AssetType>[] = [];
  private _allAvailableAssetTypes = this.enumTranslateService.translateNumericEnum(AssetType, 'i18n.shared.assetTypes.').filter((type) => (type.value as any) !== (AssetType.Unknown as any));

  constructor(
    public toastService: ToastService,
    private enumTranslateService: EnumTranslationService,
    private assetConfigurationsService: AssetConfigurationsService
  ) { }

  ngOnInit(): void {
    this.assetTypes = this._allAvailableAssetTypes;
    const config = this.assetConfigurationsService.assetConfiguration;
    if (config) {
      if (this.isBrandLogoMode) {
        this.allowedExtensions = [...config.supportedAssetLogoFileExtensions].join(',');
      } else {
        this.allowedExtensions = "";
      }
      this.deniedExtensions = config.forbiddenAssetFileExtensions.join(',');
      this.languages = config.supportedAssetLanguages;
      this.shopIds = config.supportedShopIds;
      this.supportedResolutions = config.supportedResolutionsInDpi;
    }

    this.assetUploadEditorForm.controls.type.valueChanges.subscribe({
      next: (value) => {
        const config = this.assetConfigurationsService.assetConfiguration;
        if (value === AssetType.Image) {
          this.allowedExtensions = config.supportedAssetImageFileExtensions.join(',');
        } else if (value === AssetType.Logo) {
          this.allowedExtensions = config.supportedAssetLogoFileExtensions.join(',');
        } else if (value === AssetType.LandingPage) {
          // avoid file preview reset when a file is loaded
          if (this.assetUploadEditorForm.controls.base64FileData.value == null) {
            // do not reset allowed file extensions when a file is loaded
            this.allowedExtensions = config.supportedLandingPageFileExtensions.join(',');
          }
        } else {
          this.allowedExtensions = "";
        }

        if (value === AssetType.Logo || value === AssetType.Image) {
          this.assetUploadEditorForm.controls.language.enable();
          if (value === AssetType.Logo) {
            this.assetUploadEditorForm.controls.language.disable();
          }
          this.assetUploadEditorForm.controls.language.setValidators(null);

          this.assetUploadEditorForm.controls.altText.enable();
        } else {
          this.assetUploadEditorForm.controls.language.enable();
          this.assetUploadEditorForm.controls.language.setValidators(Validators.required);

          this.assetUploadEditorForm.controls.altText.setValue(null);
          this.assetUploadEditorForm.controls.altText.disable();
        }
        this.assetUploadEditorForm.controls.language.updateValueAndValidity();
      }
    });

    //Asset type restrictions
    if (!this.isBrandLogoMode) {
      this.assetUploadEditorForm.controls.fileExtension.valueChanges.subscribe((value) => {
        this.handleFileExtensionChange(value);
      });
    }
  }

  ngAfterViewInit(): void {
    if (!this.isBrandLogoMode && this.assetUploadEditorForm.controls.fileExtension.value !== null) {
      this.handleFileExtensionChange(this.assetUploadEditorForm.controls.fileExtension.value);
    }
  }

  public onFileRemove(args: RemovingEventArgs) {
    this.removeImagePreview();
    this.assetUploadEditorForm.controls.fileExtension.setValue(null);
    this.assetUploadEditorForm.controls.base64FileData.setValue(null);
    this.assetUploadEditorForm.controls.localFilePath.setValue(null);
    const fileNames = args.filesData.map((file) => {  return file.name; });
    const origFileName = this.assetUploadEditorForm.controls.originalFileName.value;
    if (fileNames.includes(origFileName)) {
      this.assetUploadEditorForm.controls.originalFileName.setValue(null);
    }
  }

  public handleFileInput($event: SelectedEventArgs) {
    const files: FileInfo[] = $event.filesData;
    if (!files || files.length < 1) {
      return;
    }
    const file = files[0];
    var fileEndings = this.allowedExtensions ? this.allowedExtensions.replaceAll('.', '').split(',') : null;
    if ((fileEndings && !fileEndings.some((ending) => file.type.toLowerCase() === ending)) || this.deniedExtensions.replaceAll('.', '').split(',').some((ending) => file.type.toLowerCase() === ending)) {
      this.toastService.showError('i18n.domain.assets.product-assets.edit-asset.file-type-invalid', null);
      setTimeout(() => {
        this.defaultupload.clearAll();
      }, 0);
      return;
    }
    const reader = new FileReader();
    reader.onload = (event) => {
      let base64Data = event.target.result as string;
      const startIndex = base64Data.search(';base64,');
      if (startIndex > 0) {
        // replace type by cert
        base64Data = 'data:' + MimeTypeHelper.getMimeType(file.type.toLowerCase()) + base64Data.substring(startIndex);
      } else {
        base64Data = 'data:' + MimeTypeHelper.getMimeType(file.type.toLowerCase()) + ';base64,' + base64Data;
      }
      this.assetUploadEditorForm.controls.base64FileData.setValue(base64Data);
      this.assetUploadEditorForm.controls.localFilePath.setValue(file.name);
      this.assetUploadEditorForm.controls.fileExtension.setValue(file.type.toLowerCase());
      this.assetUploadEditorForm.controls.originalFileName.setValue(file.name);
    };
    reader.readAsDataURL(file.rawFile as File);

    //Display preview if image
    //https://ej2.syncfusion.com/angular/demos/#/material/uploader/image-preview
    if (file.type.toLowerCase() === "jpg" || file.type.toLowerCase() === "jpeg" || file.type.toLowerCase() === "png" || file.type.toLowerCase() === "gif" || file.type.toLowerCase() === "bmp") {
      this.removeImagePreview();
      this.parentElement = createElement('ul', { className: 'e-upload-files' });
      document.getElementsByClassName('e-upload')[0].appendChild(this.parentElement);

      this.formSelectedData(file, this);
    }
  }

  public clearUploader() {
    document.querySelector('.e-upload-file-list')?.remove();
    this.defaultupload?.clearAll();
  }

  public formSelectedData(file: FileInfo, proxy: any): void {
    let liEle: HTMLElement = createElement('li', { className: 'e-upload-file-list', attrs: { 'data-file-name': file.name } });
    let imageTag: HTMLImageElement = <HTMLImageElement>createElement('IMG', { className: 'upload-image', attrs: { 'alt': 'Image' } });
    let wrapper: HTMLElement = createElement('span', { className: 'wrapper' });
    wrapper.appendChild(imageTag);
    liEle.appendChild(wrapper);
    this.readURL(liEle, file);
    document.querySelector('.e-upload-files').appendChild(liEle);
  }

  public readURL(li: HTMLElement, args: any): void {
    let preview: HTMLImageElement = li.querySelector('.upload-image');
    let file: File = args.rawFile;
    let reader: FileReader = new FileReader();
    reader.addEventListener('load', () => { preview.src = reader.result as string; }, false);
    if (file) {
      reader.readAsDataURL(file);
    }
  }

  public replaceProductLogosSwitched() {
    this.replaceCustomizedLogos.setDisabledState(this.assetUploadEditorForm.controls.replaceProductLogos.value);
    this.replaceCustomizedLogos.checked = false;
    this.assetUploadEditorForm.controls.replaceCustomizedLogos.setValue(false);
  }

  private removeImagePreview() {
    if (!isNullOrUndefined(document.getElementById('dropArea').querySelector('.e-upload-files'))) {
      document.getElementById('dropArea').querySelector('.e-upload-files').remove();
    }
  }

  private handleFileExtensionChange(value: string) {
    if (this.assetUploadEditorForm.controls.id.value == null) {
      this.assetUploadEditorForm.controls.type.enable();
    } else {
      this.assetUploadEditorForm.controls.type.disable();
      return;
    }
    const config = this.assetConfigurationsService.assetConfiguration;
    if (config.supportedAssetDocumentFileExtensions?.find(e => e === '.' + value)) {
      // document
      const nonPdfTypes = [AssetType.Image, AssetType.Logo, AssetType.LandingPage];
      if (nonPdfTypes.find((v) => v === this.assetUploadEditorForm.controls.type.value)) {
        this.assetUploadEditorForm.controls.type.setValue(null);
      }
      this.assetTypes = this._allAvailableAssetTypes.filter((type) => nonPdfTypes.find((t) => t == type.value as any) == null);
      this.typeDropdown.refresh();
    } else if (config.supportedLandingPageFileExtensions?.find(e => e === '.' + value)) {
      // landing page
      this.typeDropdown.refresh();
      this.assetUploadEditorForm.controls.type.setValue(AssetType.LandingPage);
      this.assetUploadEditorForm.controls.type.disable();
    } else {
      // image or logo or null
      const imageTypes = [AssetType.Image, AssetType.Logo];
      if (value === null) {
        this.assetTypes = this._allAvailableAssetTypes;
      } else {
        this.assetTypes = this._allAvailableAssetTypes.filter((type) => imageTypes.find((t) => t == type.value as any));
      }
      this.typeDropdown.refresh();
      if (imageTypes.find((v) => v !== this.assetUploadEditorForm.controls.type.value)) {
        this.assetUploadEditorForm.controls.type.setValue(null);
      }
    }
  }
}
